import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Calculator, User, GraduationCap, Briefcase, Languages, Award, MapPin, Info, ArrowLeft, Download, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { performGlobalSearch } from '../services/searchAPI';
import { useSettings } from '../contexts/SettingsContext';
import settingsService from '../services/settingsService';
import { getPdfBrand } from '../utils/pdfBranding';
import ExpressEntryLabourMarket from './ExpressEntryLabourMarket';
import { calculateLabourMarket, getTorontoDate, usesNewLabourRules, totalWithLabourMarket } from '../utils/crsLaborMarket';
import {
  calculateFrenchAdditionalPoints,
  calculateSecondOfficialLanguagePoints,
  capAdditionalPoints,
  combineTransferabilityPairings,
  convertPTECoreToCLB,
  isEnglishLanguageTest,
  isFrenchLanguageTest,
  meetsCanadianEducationRequirements
} from '../utils/crsScoring';

export default function EntreeExpressCalculator() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const containerRef = useRef(null);
  const scrollPositionRef = useRef({ x: 0, y: 0 });
  const isSelectChangeRef = useRef(false);
  const isSelectOpenRef = useRef(false);
  
  // Recherche client/lead (optionnel)
  const [personQuery, setPersonQuery] = useState('');
  const [personResults, setPersonResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const searchTimeoutRef = useRef(null);
  
  // État matrimonial
  const [maritalStatus, setMaritalStatus] = useState('single');
  const [spouseIsCitizen, setSpouseIsCitizen] = useState(false);
  const [spouseAccompanying, setSpouseAccompanying] = useState(false);
  
  // Facteurs de base
  const [age, setAge] = useState('');
  const [education, setEducation] = useState(0);
  const [hasCanadianDegree, setHasCanadianDegree] = useState(false);
  const [canadianDegreeLength, setCanadianDegreeLength] = useState('none');
  const [canadianDegreeRequirements, setCanadianDegreeRequirements] = useState({
    languageStudyLimit: false,
    noReturnAward: false,
    schoolInCanada: false,
    fullTimeEightMonths: false,
    presentInCanadaEightMonths: false
  });
  
  // Tests linguistiques
  const [firstLanguageTest, setFirstLanguageTest] = useState('none');
  const [languageFirst, setLanguageFirst] = useState({ reading: 0, writing: 0, speaking: 0, listening: 0 });
  const [languageFirstScores, setLanguageFirstScores] = useState({ reading: '', writing: '', speaking: '', listening: '' }); // Scores bruts TEF/TCF
  const [secondLanguageTest, setSecondLanguageTest] = useState('none');
  const [languageSecond, setLanguageSecond] = useState({ reading: 0, writing: 0, speaking: 0, listening: 0 });
  const [languageSecondScores, setLanguageSecondScores] = useState({ reading: '', writing: '', speaking: '', listening: '' }); // Scores bruts TEF/TCF

  const secondLanguageOptions = useMemo(() => {
    const baseOptions = [{ value: 'none', label: 'Non / Sans objet' }];

    if (isFrenchLanguageTest(firstLanguageTest)) {
      return [
        ...baseOptions,
        { value: 'celpip', label: 'CELPIP-General (anglais)' },
        { value: 'ielts', label: 'IELTS General Training (anglais)' },
        { value: 'pte', label: 'PTE Core (anglais)' }
      ];
    }

    if (isEnglishLanguageTest(firstLanguageTest)) {
      return [
        ...baseOptions,
        { value: 'tef', label: 'TEF Canada (français)' },
        { value: 'tcf', label: 'TCF Canada (français)' }
      ];
    }

    return [
      ...baseOptions,
      { value: 'celpip', label: 'CELPIP-General (anglais)' },
      { value: 'ielts', label: 'IELTS General Training (anglais)' },
      { value: 'pte', label: 'PTE Core (anglais)' },
      { value: 'tef', label: 'TEF Canada (français)' },
      { value: 'tcf', label: 'TCF Canada (français)' }
    ];
  }, [firstLanguageTest]);
  
  // Expérience de travail
  const [canadianExp, setCanadianExp] = useState(0);
  const [foreignExp, setForeignExp] = useState(0);
  
  // Certificat de compétence
  const [hasCertificate, setHasCertificate] = useState(false);
  
  // Offre d'emploi
  const [hasJobOffer, setHasJobOffer] = useState(false);
  const [offerCode, setOfferCode] = useState('');
  const [labourExperience, setLabourExperience] = useState([]);
  const [experienceConfirmed, setExperienceConfirmed] = useState(false);
  const [rulesMode, setRulesMode] = useState('auto');
  const [today, setToday] = useState(getTorontoDate);
  useEffect(() => {
    const timer = setInterval(() => setToday(getTorontoDate()), 60000);
    return () => clearInterval(timer);
  }, []);
  const newLabourRules = usesNewLabourRules(rulesMode, today);
  const rulesLabel = newLabourRules
    ? `${today < '2026-11-03' ? 'Simulation — ' : ''}Grille annoncée du 3 novembre 2026`
    : 'Grille du 25 mars 2025 au 2 novembre 2026';
  const scoreSuffix = newLabourRules ? 'points estimés' : '/ 1200 points';
  
  // Frère ou sœur
  const [hasSibling, setHasSibling] = useState(false);
  
  // Facteurs du conjoint
  const [spouseEducation, setSpouseEducation] = useState(0);
  const [spouseLanguageTest, setSpouseLanguageTest] = useState('none');
  const [spouseLanguage, setSpouseLanguage] = useState({ reading: 0, writing: 0, speaking: 0, listening: 0 });
  const [spouseCanadianExp, setSpouseCanadianExp] = useState(0);
  
  // Désignation provinciale
  const [provincialNomination, setProvincialNomination] = useState(false);

  // Fonction pour générer le PDF détaillé
  const generatePDF = async () => {
    const doc = new jsPDF({ compress: true, putOnlyUsedFonts: true });
    const logoUrl = settingsService.getLogoUrl(true, settings.logo);
    const { companyName, primaryColor } = await getPdfBrand({
      companyName: settings.nom,
      logoUrl
    });
    
    // Configuration des couleurs
    const successColor = [34, 197, 94];
    const textColor = [31, 41, 55];
    const infoColor = [59, 130, 246];
    
    // Fonction pour ajouter une nouvelle page avec filigrane
    const addPageWithWatermark = async () => {
      doc.addPage();
      try {
        const logoBase64 = await getImageBase64(logoUrl);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const logoSize = 90;
        const x = (pageWidth - logoSize) / 2;
        const y = (pageHeight - logoSize) / 2;
        
        doc.setGState(new doc.GState({opacity: 0.1}));
        doc.addImage(logoBase64, 'PNG', x, y, logoSize, logoSize);
        doc.setGState(new doc.GState({opacity: 1.0}));
      } catch (error) {
        console.log('Filigrane non ajouté');
      }
    };
    
    // Fonction pour convertir l'image en base64
    const getImageBase64 = (url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function() {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          const maxSize = 200;
          let width = this.width;
          let height = this.height;
          
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(this, 0, 0, width, height);
          
          try {
            const dataURL = canvas.toDataURL('image/png', 0.8);
            resolve(dataURL);
          } catch (e) {
            reject(e);
          }
        };
        img.onerror = reject;
        img.src = url;
      });
    };
    
    // Ajouter le logo en filigrane sur la première page
    try {
      const logoBase64 = await getImageBase64(logoUrl);
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const logoSize = 90;
      const x = (pageWidth - logoSize) / 2;
      const y = (pageHeight - logoSize) / 2;
      
      doc.setGState(new doc.GState({opacity: 0.1}));
      doc.addImage(logoBase64, 'PNG', x, y, logoSize, logoSize);
      doc.setGState(new doc.GState({opacity: 1.0}));
    } catch (error) {
      console.log('Impossible de charger le logo:', error);
    }
    
    // ==================== PAGE 1: EN-TÊTE ET RÉSUMÉ ====================
    
    // En-tête
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 35, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Calculateur Entrée Express - SCG', 20, 16);
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.text('Système de classement global (Comprehensive Ranking System)', 20, 26);
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(companyName, 190, 16, { align: 'right' });
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, 190, 26, { align: 'right' });
    
    doc.setFontSize(8);
    doc.setTextColor(...textColor);
    doc.text(rulesLabel, 20, 43);
    let yPosition = 55;
    
    // Nom du client/lead si sélectionné
    if (selectedPerson) {
      doc.setFillColor(240, 240, 240);
      doc.rect(15, yPosition - 5, 180, 12, 'F');
      doc.setTextColor(...textColor);
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Calculé pour:', 20, yPosition + 2);
      doc.setFont(undefined, 'normal');
      doc.text(`${selectedPerson.label} (${selectedPerson.type === 'client' ? 'Client' : 'Lead'})`, 60, yPosition + 2);
      yPosition += 18;
    }
    
    // État matrimonial
    doc.setTextColor(...textColor);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('État matrimonial:', 20, yPosition);
    doc.setFont(undefined, 'normal');
    const maritalStatusText = {
      'single': 'Célibataire',
      'married': 'Marié(e)',
      'commonlaw': 'Conjoint de fait',
      'divorced': 'Divorcé(e)',
      'widowed': 'Veuf/Veuve'
    };
    doc.text(maritalStatusText[maritalStatus] || maritalStatus, 70, yPosition);
    
    yPosition += 6;
    if (hasSpouse) {
      doc.setFont(undefined, 'italic');
      doc.setFontSize(8);
      doc.text('(Conjoint vous accompagne)', 70, yPosition);
      yPosition += 4;
    }
    
    yPosition += 10;
    
    // Score total - Encadré
    // Le fond reste volontairement très clair : certaines couleurs de logo
    // (comme le bleu marine) seraient illisibles sur un fond foncé.
    doc.setFillColor(241, 245, 249);
    doc.rect(15, yPosition - 5, 180, 20, 'F');
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.rect(15, yPosition - 5, 180, 20);
    
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(`SCORE TOTAL: ${totalScore} ${scoreSuffix}`, 20, yPosition + 8);
    
    yPosition += 30;
    
    // ==================== SECTION A: FACTEURS DE BASE ====================
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...textColor);
    doc.text(newLabourRules ? 'A. CAPITAL HUMAIN ET INTÉGRATION AU MARCHÉ DU TRAVAIL' : 'A. FACTEURS DE BASE (CAPITAL HUMAIN)', 20, yPosition);
    doc.setLineWidth(0.3);
    doc.line(20, yPosition + 1, 190, yPosition + 1);
    
    yPosition += 8;
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    
    // Âge
    const agePoints = getAgePoints();
    doc.setFont(undefined, 'bold');
    doc.text('1. Âge:', 25, yPosition);
    doc.text(`${agePoints} pts`, 170, yPosition);
    yPosition += 5;
    doc.setFont(undefined, 'normal');
    doc.text(`• Âge: ${age} ans`, 30, yPosition);
    yPosition += 5;
    doc.text(`• Points maximum: ${hasSpouse ? 100 : 110} pts (20-29 ans)`, 30, yPosition);
    
    yPosition += 10;
    
    // Niveau d'études
    const eduPoints = getEducationPoints();
    doc.setFont(undefined, 'bold');
    doc.text('2. Niveau d\'études:', 25, yPosition);
    doc.text(`${eduPoints} pts`, 170, yPosition);
    yPosition += 5;
    doc.setFont(undefined, 'normal');
    const eduText = {
      0: 'Études secondaires non complétées',
      28: 'Diplôme d\'études secondaires',
      84: 'Diplôme postsecondaire (1 an)',
      91: 'Diplôme postsecondaire (2 ans)',
      112: 'Diplôme postsecondaire (3 ans+)',
      119: 'Deux diplômes ou plus (dont un de 3 ans+)',
      126: 'Diplôme universitaire (niveau maîtrise ou diplôme professionnel)',
      140: 'Doctorat (Ph.D.)'
    };
    doc.text(`• ${eduText[education] || 'Non spécifié'}`, 30, yPosition);
    yPosition += 5;
    if (hasCanadianDegree) {
      doc.text(`• Diplôme canadien admissible: ${isCanadianDegreeEligible ? 'Oui' : 'Non'}`, 30, yPosition);
      yPosition += 5;
      if (isCanadianDegreeEligible) {
        doc.text(`• Durée du diplôme canadien: ${canadianDegreeLength}`, 30, yPosition);
        yPosition += 5;
      }
    }
    doc.text(`• Points maximum: ${hasSpouse ? 140 : 150} pts`, 30, yPosition);
    
    yPosition += 10;
    
    // Première langue officielle
    const lang1Points = getLanguagePoints(languageFirst);
    doc.setFont(undefined, 'bold');
    doc.text('3. Première langue officielle:', 25, yPosition);
    doc.text(`${lang1Points} pts`, 170, yPosition);
    yPosition += 5;
    doc.setFont(undefined, 'normal');
    const testNames = {
      'none': 'Aucun test',
      'ielts': 'IELTS (General)',
      'celpip': 'CELPIP-G',
      'pte': 'PTE Core',
      'tef': 'TEF Canada',
      'tcf': 'TCF Canada'
    };
    doc.text(`• Test: ${testNames[firstLanguageTest] || 'Non spécifié'}`, 30, yPosition);
    yPosition += 5;
    doc.text(`• Écoute: NCLC ${languageFirst.listening} | Lecture: NCLC ${languageFirst.reading}`, 30, yPosition);
    yPosition += 5;
    doc.text(`• Expression orale: NCLC ${languageFirst.speaking} | Expression écrite: NCLC ${languageFirst.writing}`, 30, yPosition);
    yPosition += 5;
    doc.text(`• Points maximum: ${hasSpouse ? 128 : 136} pts (NCLC 10+ dans les 4 compétences)`, 30, yPosition);
    
    yPosition += 10;
    
    // Deuxième langue officielle
    const lang2Points = getSecondLanguagePoints(languageSecond);
    doc.setFont(undefined, 'bold');
    doc.text('4. Deuxième langue officielle:', 25, yPosition);
    doc.text(`${lang2Points} pts`, 170, yPosition);
    yPosition += 5;
    doc.setFont(undefined, 'normal');
    if (secondLanguageTest !== 'none') {
      doc.text(`• Test: ${testNames[secondLanguageTest] || 'Non spécifié'}`, 30, yPosition);
      yPosition += 5;
      doc.text(`• Écoute: NCLC ${languageSecond.listening} | Lecture: NCLC ${languageSecond.reading}`, 30, yPosition);
      yPosition += 5;
      doc.text(`• Expression orale: NCLC ${languageSecond.speaking} | Expression écrite: NCLC ${languageSecond.writing}`, 30, yPosition);
      yPosition += 5;
    } else {
      doc.text(`• Aucun test de deuxième langue`, 30, yPosition);
      yPosition += 5;
    }
    doc.text(`• Points maximum: ${hasSpouse ? 22 : 24} pts`, 30, yPosition);
    
    yPosition += 10;
    
    // Expérience de travail canadienne
    const canExpPoints = getCanadianExpPoints();
    doc.setFont(undefined, 'bold');
    doc.text(newLabourRules ? '5. Intégration au marché du travail:' : '5. Expérience de travail au Canada:', 25, yPosition);
    doc.text(`${canExpPoints} pts`, 170, yPosition);
    yPosition += 5;
    doc.setFont(undefined, 'normal');
    doc.text(`• Années d'expérience: ${canadianExp} an(s)`, 30, yPosition);
    yPosition += 5;
    doc.text(newLabourRules
      ? `• Expérience: ${labourResult.duration} + ${labourResult.bonus} pts; offre: ${labourResult.offer} pts. Meilleur résultat retenu.`
      : `• Points maximum: ${hasSpouse ? 70 : 80} pts (5 ans ou plus)`, 30, yPosition);
    
    yPosition += 12;
    
    // Total Section A
    const totalA = agePoints + eduPoints + lang1Points + lang2Points + canExpPoints;
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPosition - 5, 170, 10, 'F');
    doc.setFont(undefined, 'bold');
    doc.setFontSize(10);
    doc.text('TOTAL SECTION A:', 25, yPosition + 2);
    doc.text(newLabourRules ? `${totalA} pts` : `${totalA} / ${hasSpouse ? 460 : 500} pts`, 160, yPosition + 2);
    
    // ==================== PAGE 2: SECTIONS B, C, D ====================
    
    await addPageWithWatermark();
    yPosition = 20;
    
    // ==================== SECTION B: FACTEURS DU CONJOINT ====================
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...textColor);
    doc.text('B. FACTEURS DU CONJOINT', 20, yPosition);
    doc.setLineWidth(0.3);
    doc.line(20, yPosition + 1, 190, yPosition + 1);
    
    yPosition += 8;
    doc.setFontSize(9);
    
    if (hasSpouse) {
      // Niveau d'études du conjoint
      const spouseEduPoints = getSpouseEducationPoints();
      doc.setFont(undefined, 'bold');
      doc.text('1. Niveau d\'études du conjoint:', 25, yPosition);
      doc.text(`${spouseEduPoints} pts`, 170, yPosition);
      yPosition += 5;
      doc.setFont(undefined, 'normal');
      doc.text(`• ${eduText[spouseEducation] || 'Non spécifié'}`, 30, yPosition);
      yPosition += 5;
      doc.text(`• Points maximum: 10 pts`, 30, yPosition);
      
      yPosition += 10;
      
      // Compétences linguistiques du conjoint
      const spouseLangPoints = getSpouseLanguagePoints();
      doc.setFont(undefined, 'bold');
      doc.text('2. Compétences linguistiques du conjoint:', 25, yPosition);
      doc.text(`${spouseLangPoints} pts`, 170, yPosition);
      yPosition += 5;
      doc.setFont(undefined, 'normal');
      if (spouseLanguageTest !== 'none') {
        doc.text(`• Test: ${testNames[spouseLanguageTest] || 'Non spécifié'}`, 30, yPosition);
        yPosition += 5;
        doc.text(`• Écoute: NCLC ${spouseLanguage.listening} | Lecture: NCLC ${spouseLanguage.reading}`, 30, yPosition);
        yPosition += 5;
        doc.text(`• Expression orale: NCLC ${spouseLanguage.speaking} | Expression écrite: NCLC ${spouseLanguage.writing}`, 30, yPosition);
        yPosition += 5;
      } else {
        doc.text(`• Aucun test`, 30, yPosition);
        yPosition += 5;
      }
      doc.text(`• Points maximum: 20 pts (NCLC 9+ dans les 4 compétences)`, 30, yPosition);
      
      yPosition += 10;
      
      // Expérience canadienne du conjoint
      const spouseExpPoints = getSpouseCanadianExpPoints();
      doc.setFont(undefined, 'bold');
      doc.text('3. Expérience de travail du conjoint:', 25, yPosition);
      doc.text(`${spouseExpPoints} pts`, 170, yPosition);
      yPosition += 5;
      doc.setFont(undefined, 'normal');
      doc.text(`• Années d'expérience au Canada: ${spouseCanadianExp} an(s)`, 30, yPosition);
      yPosition += 5;
      doc.text(`• Points maximum: 10 pts (5 ans ou plus)`, 30, yPosition);
    } else {
      doc.setFont(undefined, 'italic');
      doc.text('Pas de conjoint accompagnant', 25, yPosition);
    }
    
    yPosition += 12;
    
    // Total Section B
    const totalB = hasSpouse ? (getSpouseEducationPoints() + getSpouseLanguagePoints() + getSpouseCanadianExpPoints()) : 0;
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPosition - 5, 170, 10, 'F');
    doc.setFont(undefined, 'bold');
    doc.setFontSize(10);
    doc.text('TOTAL SECTION B:', 25, yPosition + 2);
    doc.text(`${totalB} / 40 pts`, 160, yPosition + 2);
    
    yPosition += 18;
    
    // ==================== SECTION C: TRANSFÉRABILITÉ ====================
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('C. FACTEURS DE TRANSFÉRABILITÉ', 20, yPosition);
    doc.setLineWidth(0.3);
    doc.line(20, yPosition + 1, 190, yPosition + 1);
    
    yPosition += 8;
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    
    const transferPoints = getTransferabilityPoints();
    doc.text('Combinaisons de facteurs:', 25, yPosition);
    yPosition += 6;
    doc.text('• Études + compétences linguistiques (1ère langue)', 30, yPosition);
    yPosition += 5;
    doc.text('• Études + expérience de travail canadienne', 30, yPosition);
    yPosition += 5;
    doc.text('• Compétences linguistiques + expérience étrangère', 30, yPosition);
    yPosition += 5;
    doc.text('• Expérience canadienne + expérience étrangère', 30, yPosition);
    yPosition += 5;
    doc.text('• Certificat de compétence + compétences linguistiques', 30, yPosition);
    
    yPosition += 10;
    
    doc.setFont(undefined, 'bold');
    doc.text('Certificat de compétence:', 25, yPosition);
    doc.setFont(undefined, 'normal');
    doc.text(hasCertificate ? 'Oui' : 'Non', 100, yPosition);
    
    yPosition += 7;
    
    doc.setFont(undefined, 'bold');
    doc.text('Expérience de travail étrangère:', 25, yPosition);
    doc.setFont(undefined, 'normal');
    doc.text(`${foreignExp} an(s)`, 100, yPosition);
    
    yPosition += 12;
    
    // Total Section C
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPosition - 5, 170, 10, 'F');
    doc.setFont(undefined, 'bold');
    doc.setFontSize(10);
    doc.text('TOTAL SECTION C:', 25, yPosition + 2);
    doc.text(`${transferPoints} / 100 pts`, 160, yPosition + 2);
    
    yPosition += 18;
    
    // ==================== SECTION D: POINTS ADDITIONNELS ====================
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('D. POINTS ADDITIONNELS', 20, yPosition);
    doc.setLineWidth(0.3);
    doc.line(20, yPosition + 1, 190, yPosition + 1);
    
    yPosition += 8;
    doc.setFontSize(9);
    
    const additionalPoints = getAdditionalPoints();
    
    // Frère ou sœur au Canada
    doc.setFont(undefined, 'bold');
    doc.text('1. Frère ou sœur au Canada:', 25, yPosition);
    doc.text(hasSibling ? '15 pts' : '0 pt', 170, yPosition);
    yPosition += 5;
    doc.setFont(undefined, 'normal');
    doc.text(hasSibling ? '• Citoyen ou résident permanent au Canada' : '• Aucun', 30, yPosition);
    
    yPosition += 10;
    
    // Compétences linguistiques françaises
    doc.setFont(undefined, 'bold');
    doc.text('2. Compétences linguistiques en français:', 25, yPosition);
    const frenchBonus = getFrenchPoints();
    const frenchSkills = isFrenchLanguageTest(firstLanguageTest)
      ? languageFirst
      : isFrenchLanguageTest(secondLanguageTest)
        ? languageSecond
        : null;
    doc.text(`${frenchBonus} pts`, 170, yPosition);
    yPosition += 5;
    doc.setFont(undefined, 'normal');
    if (frenchBonus > 0) {
      doc.text(`• NCLC ${Math.min(...Object.values(frenchSkills))}+ en français`, 30, yPosition);
    } else {
      doc.text(`• Aucun`, 30, yPosition);
    }

    yPosition += 10;

    // Études postsecondaires au Canada
    const canadianEducationPoints = getCanadianEducationPoints();
    doc.setFont(undefined, 'bold');
    doc.text('3. Études postsecondaires au Canada:', 25, yPosition);
    doc.text(`${canadianEducationPoints} pts`, 170, yPosition);
    yPosition += 5;
    doc.setFont(undefined, 'normal');
    doc.text(isCanadianDegreeEligible ? '• Conditions IRCC confirmées' : '• Aucun diplôme admissible confirmé', 30, yPosition);

    yPosition += 10;

    // Désignation provinciale
    doc.setFont(undefined, 'bold');
    doc.text('4. Désignation d\'une province:', 25, yPosition);
    doc.text(provincialNomination ? '600 pts' : '0 pt', 170, yPosition);
    yPosition += 5;
    doc.setFont(undefined, 'normal');
    doc.text(provincialNomination ? '• Certificat de désignation provincial valide' : '• Aucun', 30, yPosition);
    
    yPosition += 10;
    
    // L'offre est comparée à l'expérience dans A, jamais ajoutée une seconde fois dans D.
    doc.setFont(undefined, 'bold');
    doc.text('5. Offre d\'emploi réservé:', 25, yPosition);
    doc.text(newLabourRules ? 'Voir section A' : '0 pt', 165, yPosition);
    yPosition += 5;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...infoColor);
    doc.setFontSize(8);
    doc.text(newLabourRules ? `• CNP de l'offre: ${hasJobOffer ? offerCode || 'Non renseigné' : 'Aucune'}; résultat potentiel: ${labourResult.offer} pts.` : '• Offre d’emploi: aucun point SCG dans la grille antérieure au 3 novembre 2026.', 30, yPosition);
    yPosition += 4;
    doc.text(newLabourRules ? '  Comparée à l’expérience en section A; aucun cumul ni point additionnel ici.' : '  Une offre peut cependant contribuer à l’admissibilité à certains programmes.', 30, yPosition);
    doc.setTextColor(...textColor);
    doc.setFontSize(9);
    
    yPosition += 10;
    
    // Total Section D
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPosition - 5, 170, 10, 'F');
    doc.setFont(undefined, 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...textColor);
    doc.text('TOTAL SECTION D:', 25, yPosition + 2);
    doc.text(`${additionalPoints} / 600 pts`, 160, yPosition + 2);
    
    yPosition += 15;
    
    // Vérifier s'il faut ajouter une nouvelle page pour le récapitulatif
    if (yPosition > 230) {
      await addPageWithWatermark();
      yPosition = 20;
    }
    
    // ==================== RÉCAPITULATIF FINAL ====================
    
    doc.setFillColor(245, 245, 245);
    doc.rect(15, yPosition, 180, 42, 'F');
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.8);
    doc.rect(15, yPosition, 180, 42);
    
    yPosition += 8;
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('RÉCAPITULATIF', 20, yPosition);
    
    yPosition += 7;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Section A - Facteurs de base: ${totalA} pts`, 25, yPosition);
    yPosition += 5;
    doc.text(`Section B - Facteurs du conjoint: ${totalB} pts`, 25, yPosition);
    yPosition += 5;
    doc.text(`Section C - Transférabilité: ${transferPoints} pts`, 25, yPosition);
    yPosition += 5;
    doc.text(`Section D - Points additionnels: ${additionalPoints} pts`, 25, yPosition);
    
    yPosition += 7;
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(`SCORE TOTAL: ${totalScore} ${scoreSuffix}`, 25, yPosition);
    
    yPosition += 12;
    
    // Note de bas de page
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Note importante — estimation personnalisée', 20, yPosition);
    yPosition += 4;
    doc.setFont(undefined, 'normal');
    doc.text('Estimation basée sur vos informations et la grille indiquée en première page. Une simulation de la', 20, yPosition);
    yPosition += 3.5;
    doc.text('nouvelle grille ne constitue pas une note officielle. Une invitation à présenter une demande (IPD)', 20, yPosition);
    yPosition += 3.5;
    doc.text('ne peut être garantie. Les seuils et critères d\'IRCC peuvent changer à chaque ronde.', 20, yPosition);
    if (newLabourRules) {
      yPosition += 5;
      doc.text('Réforme annoncée : somme estimée des facteurs; le plafond global révisé reste à confirmer.', 20, yPosition);
    }
    
    // Sauvegarde
    const fileName = `entree-express-scg-detaille-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  // Déterminer si a un conjoint
  const hasSpouse = (maritalStatus === 'married' || maritalStatus === 'commonlaw') &&
                     !spouseIsCitizen && spouseAccompanying;
  const isCanadianDegreeEligible = hasCanadianDegree &&
    meetsCanadianEducationRequirements(canadianDegreeRequirements);

  // Fonctions de conversion des scores TEF Canada vers NCLC
  const convertTEFtoNCLC = (score, skill) => {
    // Barème officiel TEF Canada → NCLC
    if (skill === 'reading') { // Compréhension écrite
      if (score >= 263) return 10;
      if (score >= 248) return 9;
      if (score >= 233) return 8;
      if (score >= 207) return 7;
      if (score >= 181) return 6;
      if (score >= 151) return 5;
      if (score >= 121) return 4;
      return 0;
    } else if (skill === 'writing') { // Expression écrite
      if (score >= 393) return 10;
      if (score >= 371) return 9;
      if (score >= 349) return 8;
      if (score >= 310) return 7;
      if (score >= 271) return 6;
      if (score >= 226) return 5;
      if (score >= 181) return 4;
      return 0;
    } else if (skill === 'listening') { // Compréhension orale
      if (score >= 316) return 10;
      if (score >= 298) return 9;
      if (score >= 280) return 8;
      if (score >= 249) return 7;
      if (score >= 217) return 6;
      if (score >= 181) return 5;
      if (score >= 145) return 4;
      return 0;
    } else if (skill === 'speaking') { // Expression orale
      if (score >= 393) return 10;
      if (score >= 371) return 9;
      if (score >= 349) return 8;
      if (score >= 310) return 7;
      if (score >= 271) return 6;
      if (score >= 226) return 5;
      if (score >= 181) return 4;
      return 0;
    }
    return 0;
  };

  // Fonctions de conversion des scores TCF Canada vers NCLC
  const convertTCFtoNCLC = (score, skill) => {
    // Barème officiel TCF Canada → NCLC
    if (skill === 'reading') { // Compréhension écrite
      if (score >= 549) return 10;
      if (score >= 524) return 9;
      if (score >= 499) return 8;
      if (score >= 453) return 7;
      if (score >= 406) return 6;
      if (score >= 375) return 5;
      if (score >= 342) return 4;
      return 0;
    } else if (skill === 'writing') { // Expression écrite
      if (score >= 16) return 10;
      if (score >= 14) return 9;
      if (score >= 12) return 8;
      if (score >= 10) return 7;
      if (score >= 7) return 6;
      if (score >= 6) return 5;
      if (score >= 4) return 4;
      return 0;
    } else if (skill === 'listening') { // Compréhension orale
      if (score >= 549) return 10;
      if (score >= 523) return 9;
      if (score >= 503) return 8;
      if (score >= 458) return 7;
      if (score >= 398) return 6;
      if (score >= 369) return 5;
      if (score >= 331) return 4;
      return 0;
    } else if (skill === 'speaking') { // Expression orale
      if (score >= 16) return 10;
      if (score >= 14) return 9;
      if (score >= 12) return 8;
      if (score >= 10) return 7;
      if (score >= 7) return 6;
      if (score >= 6) return 5;
      if (score >= 4) return 4;
      return 0;
    }
    return 0;
  };

  // Fonctions de conversion des scores IELTS General Training vers CLB
  const convertIELTStoCLB = (score) => {
    // Barème officiel IELTS → CLB (identique pour toutes les compétences selon le tableau)
    const scoreNum = Number(score) || 0;
    if (scoreNum >= 8.5) return 10;
    if (scoreNum >= 8.0) return 9;
    if (scoreNum >= 7.5) return 8;
    if (scoreNum >= 7.0) return 9; // Lecture: 8.0=9, mais 7.0=7 selon tableau
    if (scoreNum >= 6.5) return 8; // Varie selon compétence
    if (scoreNum >= 6.0) return 7;
    if (scoreNum >= 5.5) return 6;
    if (scoreNum >= 5.0) return 5;
    if (scoreNum >= 4.5) return 4; // Écoute: 4.5=4
    if (scoreNum >= 4.0) return 4; // Expression écrite: 4.0=4
    if (scoreNum >= 3.5) return 4; // Lecture: 3.5=4
    return 0;
  };

  // Conversion IELTS détaillée par compétence (selon tableau fourni)
  const convertIELTStoCLBBySkill = (score, skill) => {
    const scoreNum = Number(score) || 0;
    
    if (skill === 'reading') { // Compréhension de l'écrit
      if (scoreNum >= 8.0) return 10;
      if (scoreNum >= 7.0) return 9;
      if (scoreNum >= 6.5) return 8;
      if (scoreNum >= 6.0) return 7;
      if (scoreNum >= 5.0) return 6;
      if (scoreNum >= 4.0) return 5;
      if (scoreNum >= 3.5) return 4;
      return 0;
    } else if (skill === 'writing') { // Expression écrite
      if (scoreNum >= 7.5) return 10;
      if (scoreNum >= 7.0) return 9;
      if (scoreNum >= 6.5) return 8;
      if (scoreNum >= 6.0) return 7;
      if (scoreNum >= 5.5) return 6;
      if (scoreNum >= 5.0) return 5;
      if (scoreNum >= 4.0) return 4;
      return 0;
    } else if (skill === 'listening') { // Compréhension de l'oral
      if (scoreNum >= 8.5) return 10;
      if (scoreNum >= 8.0) return 9;
      if (scoreNum >= 7.5) return 8;
      if (scoreNum >= 6.0) return 7;
      if (scoreNum >= 5.5) return 6;
      if (scoreNum >= 5.0) return 5;
      if (scoreNum >= 4.5) return 4;
      return 0;
    } else if (skill === 'speaking') { // Expression orale
      if (scoreNum >= 7.5) return 10;
      if (scoreNum >= 7.0) return 9;
      if (scoreNum >= 6.5) return 8;
      if (scoreNum >= 6.0) return 7;
      if (scoreNum >= 5.5) return 6;
      if (scoreNum >= 5.0) return 5;
      if (scoreNum >= 4.0) return 4;
      return 0;
    }
    return 0;
  };

  // Fonctions de conversion des scores CELPIP-General vers CLB
  const convertCELPIPtoCLB = (score) => {
    // Pour CELPIP, le score est directement le niveau CLB (score 10 = CLB 10, etc.)
    const scoreNum = Number(score) || 0;
    if (scoreNum >= 10) return 10;
    if (scoreNum >= 9) return 9;
    if (scoreNum >= 8) return 8;
    if (scoreNum >= 7) return 7;
    if (scoreNum >= 6) return 6;
    if (scoreNum >= 5) return 5;
    if (scoreNum >= 4) return 4;
    return 0;
  };

  // Calculer les points d'âge
  const getAgePoints = () => {
    const ageNum = Number(age) || 0;
    const agePoints = {
      17: [0, 0], 18: [90, 99], 19: [95, 105], 20: [100, 110], 21: [100, 110],
      22: [100, 110], 23: [100, 110], 24: [100, 110], 25: [100, 110],
      26: [100, 110], 27: [100, 110], 28: [100, 110], 29: [100, 110],
      30: [95, 105], 31: [90, 99], 32: [85, 94], 33: [80, 88],
      34: [75, 83], 35: [70, 77], 36: [65, 72], 37: [60, 66],
      38: [55, 61], 39: [50, 55], 40: [45, 50], 41: [35, 39],
      42: [25, 28], 43: [15, 17], 44: [5, 6]
    };
    
    if (ageNum <= 17) return 0;
    if (ageNum >= 45) return 0;
    const points = agePoints[ageNum];
    return hasSpouse ? points[0] : points[1];
  };

  // Calculer les points d'études
  const getEducationPoints = () => {
    const eduPoints = {
      0: [0, 0], 28: [28, 30], 84: [84, 90], 91: [91, 98],
      112: [112, 120], 119: [119, 128], 126: [126, 135], 140: [140, 150]
    };
    const points = eduPoints[education] || [0, 0];
    return hasSpouse ? points[0] : points[1];
  };

  // Calculer les points linguistiques pour la première langue
  const getLanguagePoints = (skills) => {
    const nclcToPoints = (nclc, withSpouse) => {
      if (nclc < 4) return 0;
      if (nclc <= 5) return 6;
      if (nclc === 6) return withSpouse ? 8 : 9;
      if (nclc === 7) return withSpouse ? 16 : 17;
      if (nclc === 8) return withSpouse ? 22 : 23;
      if (nclc === 9) return withSpouse ? 29 : 31;
      return withSpouse ? 32 : 34;
    };
    
    return Object.values(skills).reduce((sum, nclc) => 
      sum + nclcToPoints(nclc, hasSpouse), 0);
  };

  // Calculer les points de la deuxième langue
  const getSecondLanguagePoints = (skills) => {
    return calculateSecondOfficialLanguagePoints(skills, hasSpouse);
  };

  // Calculer les points d'expérience canadienne
  const labourResult = calculateLabourMarket({ years: canadianExp, hasSpouse, newRules: newLabourRules,
    experience: labourExperience, experienceConfirmed, hasJobOffer, offerCode });
  const getCanadianExpPoints = () => labourResult.total;

  // Facteurs du conjoint
  const getSpouseEducationPoints = () => {
    const points = { 0: 0, 2: 2, 6: 6, 7: 7, 8: 8, 9: 9, 10: 10 };
    return hasSpouse ? (points[spouseEducation] !== undefined ? points[spouseEducation] : 0) : 0;
  };

  const getSpouseLanguagePoints = () => {
    if (!hasSpouse) return 0;
    const nclcToPoints = (nclc) => {
      if (nclc <= 4) return 0;
      if (nclc <= 6) return 1;
      if (nclc <= 8) return 3;
      return 5;
    };
    return Object.values(spouseLanguage).reduce((sum, nclc) => 
      sum + nclcToPoints(nclc), 0);
  };

  const getSpouseCanadianExpPoints = () => {
    if (!hasSpouse) return 0;
    const points = { 0: 0, 1: 5, 2: 7, 3: 8, 4: 9, 5: 10 };
    return points[spouseCanadianExp] !== undefined ? points[spouseCanadianExp] : 0;
  };

  // Facteurs liés au transfert des compétences
  const getTransferabilityPoints = () => {
    const minLanguageLevel = Math.min(...Object.values(languageFirst));
    const hasGoodLanguage = minLanguageLevel >= 7;
    const hasExcellentLanguage = minLanguageLevel >= 9;
    
    // 1. Sous-total Éducation (max 50 points)
    let educationA = 0;
    let educationB = 0;
    
    if (hasGoodLanguage && education >= 84) {
      if (education >= 119) {
        educationA = hasExcellentLanguage ? 50 : 25;
      } else if (education >= 84) {
        educationA = hasExcellentLanguage ? 25 : 13;
      }
    }
    
    if (canadianExp >= 1 && education >= 84) {
      if (education >= 119) {
        educationB = canadianExp >= 2 ? 50 : 25;
      } else if (education >= 84) {
        educationB = canadianExp >= 2 ? 25 : 13;
      }
    }
    
    const educationSubtotal = combineTransferabilityPairings(educationA, educationB);
    
    // 2. Sous-total Expérience étrangère (max 50 points)
    let foreignA = 0;
    let foreignB = 0;
    
    if (hasGoodLanguage && foreignExp >= 1) {
      if (foreignExp >= 3) {
        foreignA = hasExcellentLanguage ? 50 : 25;
      } else {
        foreignA = hasExcellentLanguage ? 25 : 13;
      }
    }
    
    if (canadianExp >= 1 && foreignExp >= 1) {
      if (canadianExp >= 2) {
        foreignB = foreignExp >= 3 ? 50 : 25;
      } else {
        foreignB = foreignExp >= 3 ? 25 : 13;
      }
    }
    
    const foreignSubtotal = combineTransferabilityPairings(foreignA, foreignB);
    
    // 3. Sous-total Certificat (max 50 points)
    let certificateSubtotal = 0;
    if (hasCertificate && minLanguageLevel >= 5) {
      certificateSubtotal = minLanguageLevel >= 7 ? 50 : 25;
    }
    
    const total = educationSubtotal + foreignSubtotal + certificateSubtotal;
    return Math.min(total, 100);
  };

  // Points français
  const getFrenchPoints = () => {
    return calculateFrenchAdditionalPoints({
      firstLanguageTest,
      secondLanguageTest,
      languageFirst,
      languageSecond
    });
  };
  // Points d'études canadiennes
  const getCanadianEducationPoints = () => {
    if (!isCanadianDegreeEligible) return 0;
    if (canadianDegreeLength === 'short') return 15;
    if (canadianDegreeLength === 'long') return 30;
    return 0;
  };

  // Points supplémentaires
  const getAdditionalPoints = () => {
    let points = 0;
    if (hasSibling) points += 15;
    points += getFrenchPoints();
    points += getCanadianEducationPoints();
    if (provincialNomination) points += 600;
    return capAdditionalPoints(points);
  };

  // Calculer le score total - simple calcul direct comme dans ImmigrationCalculator
  const calculateTotalScore = () => {
    const corePoints = 
      getAgePoints() + 
      getEducationPoints() + 
      getLanguagePoints(languageFirst) + 
      getSecondLanguagePoints(languageSecond) + 
      getCanadianExpPoints();
    
    const spousePoints = 
      getSpouseEducationPoints() + 
      getSpouseLanguagePoints() + 
      getSpouseCanadianExpPoints();
    
    const transferPoints = getTransferabilityPoints();
    const additionalPoints = getAdditionalPoints();
    
    return totalWithLabourMarket({ otherCore: corePoints - labourResult.total, spouse: spousePoints,
      transfer: transferPoints, additional: additionalPoints, labour: labourResult, newRules: newLabourRules });
  };

  const totalScore = calculateTotalScore();

  // Recherche client/lead
  useEffect(() => {
    if (!personQuery || personQuery.trim().length < 2) {
      setPersonResults([]);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const res = await performGlobalSearch(personQuery.trim());
        const data = res?.data || {};
        const leads = (data.leads || []).map(l => ({
          id: l.id,
          type: 'lead',
          label: l.title || l.subtitle || 'Lead',
          raw: l
        }));
        const clients = (data.clients || []).map(c => ({
          id: c.id,
          type: 'client',
          label: c.title || c.subtitle || 'Client',
          raw: c
        }));
        setPersonResults([...clients.slice(0, 5), ...leads.slice(0, 5)]);
      } catch (e) {
        setPersonResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => searchTimeoutRef.current && clearTimeout(searchTimeoutRef.current);
  }, [personQuery]);

  // Réinitialiser le deuxième test si incompatible avec le premier
  useEffect(() => {
    // Si première langue est français et deuxième est aussi français, réinitialiser
    if (isFrenchLanguageTest(firstLanguageTest) && isFrenchLanguageTest(secondLanguageTest)) {
      setSecondLanguageTest('none');
      setLanguageSecond({ reading: 0, writing: 0, speaking: 0, listening: 0 });
      setLanguageSecondScores({ reading: '', writing: '', speaking: '', listening: '' });
    }
    // Si première langue est anglais et deuxième est aussi anglais, réinitialiser
    if (isEnglishLanguageTest(firstLanguageTest) && isEnglishLanguageTest(secondLanguageTest)) {
      setSecondLanguageTest('none');
      setLanguageSecond({ reading: 0, writing: 0, speaking: 0, listening: 0 });
      setLanguageSecondScores({ reading: '', writing: '', speaking: '', listening: '' });
    }
  }, [firstLanguageTest, secondLanguageTest]);

  // Le scroll est désormais géré par des composants de sélection personnalisés.

  // Gestionnaire générique pour simplifier les champs contrôlés
  const handleSelectChange = (setter) => (e) => {
    setter(e.target.value);
  };

  const handleFirstLanguageTestChange = (newValue) => {
    setFirstLanguageTest(newValue);
    setLanguageFirst({ reading: 0, writing: 0, speaking: 0, listening: 0 });
    setLanguageFirstScores({ reading: '', writing: '', speaking: '', listening: '' });
  };

  const handleSecondLanguageTestChange = (newValue) => {
    setSecondLanguageTest(newValue);
    setLanguageSecond({ reading: 0, writing: 0, speaking: 0, listening: 0 });
    setLanguageSecondScores({ reading: '', writing: '', speaking: '', listening: '' });
  };

  const handleCanadianDegreeChange = (checked) => {
    setHasCanadianDegree(checked);
    if (!checked) {
      setCanadianDegreeLength('none');
      setCanadianDegreeRequirements({
        languageStudyLimit: false,
        noReturnAward: false,
        schoolInCanada: false,
        fullTimeEightMonths: false,
        presentInCanadaEightMonths: false
      });
    }
  };

  const handleCanadianDegreeRequirementChange = (requirement, checked) => {
    setCanadianDegreeRequirements((current) => ({
      ...current,
      [requirement]: checked
    }));
  };

  // Ces composants sont mémorisés pour conserver les champs de saisie montés
  // pendant les recalculs de score. Sans cela, chaque frappe recréait la section
  // entière, retirait le focus du champ et faisait remonter la page.
  const InfoBox = useMemo(() => ({ children, type = 'info' }) => {
    const colors = {
      info: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' },
      tip: { bg: '#F0FDF4', border: '#BBF7D0', text: '#15803D' },
      warning: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' }
    };
    
    const color = colors[type];
    
    return (
      <div className={`express-entry-info express-entry-info--${type}`} style={{
        backgroundColor: color.bg,
        borderLeft: `4px solid ${color.border}`, 
        color: color.text,
        padding: '16px',
        borderRadius: '8px',
        fontSize: '14px',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Info style={{ width: '20px', height: '20px', flexShrink: 0, marginTop: '2px' }} />
          <div>{children}</div>
        </div>
      </div>
    );
  }, []);

  const Section = useMemo(() => ({ title, icon, children }) => (
    <section className="express-entry-section" style={{ marginBottom: '32px' }}>
      <h2 className="express-entry-section-title" style={{
        fontSize: '20px', 
        fontWeight: 'bold', 
        color: '#1F2937', 
        borderBottom: '2px solid #DC2626', 
        paddingBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '16px'
      }}>
        {icon}
        {title}
      </h2>
      {children}
    </section>
  ), []);

  const FormBox = useMemo(() => ({ children }) => (
    <div className="express-entry-form-box" style={{ backgroundColor: '#f9fafb', padding: '24px', borderRadius: '12px', marginBottom: '16px' }}>
      {children}
    </div>
  ), []);

  const CustomSelect = ({
    value,
    onChange,
    options,
    placeholder = 'Sélectionner...',
    disabled = false,
    style = {},
    optionHeight = 42
  }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (open && containerRef.current && !containerRef.current.contains(event.target)) {
          setOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [open]);

    const selectedOption = options.find((option) => option.value === value);

    const handleKeyDown = (event) => {
      if (disabled) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        setOpen((prev) => !prev);
      }

      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    const handleOptionClick = (option) => {
      if (disabled) {
        return;
      }

      onChange(option.value);
      setOpen(false);
    };

    return (
      <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
        <button
          className="express-entry-select"
          type="button"
          onClick={() => !disabled && setOpen((prev) => !prev)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: disabled ? '#F3F4F6' : '#fff',
            color: disabled ? '#9CA3AF' : '#111827',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: disabled ? 'not-allowed' : 'pointer',
            ...style
          }}
        >
          <span style={{ flex: 1, textAlign: 'left' }}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown size={16} color={disabled ? '#9CA3AF' : '#374151'} />
        </button>

        {open && (
          <div
            className="express-entry-options"
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 20px 25px -15px rgba(0, 0, 0, 0.3)',
              zIndex: 9999,
              maxHeight: optionHeight * 6,
              overflowY: 'auto'
            }}
            role="listbox"
          >
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <div
                  className="express-entry-option"
                  key={String(option.value)}
                  onClick={() => handleOptionClick(option)}
                  role="option"
                  aria-selected={isSelected}
                  style={{
                    padding: '10px 14px',
                    fontSize: '14px',
                    backgroundColor: isSelected ? '#F9FAFB' : '#fff',
                    color: '#111827',
                    cursor: 'pointer',
                    borderBottom: '1px solid #F3F4F6'
                  }}
                >
                  {option.label}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Aucun style global supplémentaire requis : les CustomSelect empêchent le scroll indésirable.

  return (
    <div className="express-entry-calculator" ref={containerRef} style={{ maxWidth: '1400px', margin: '0 auto', scrollBehavior: 'auto' }}>
      <div className="express-entry-surface" style={{
        backgroundColor: 'white', 
        borderRadius: '8px', 
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
        padding: '32px',
        scrollBehavior: 'auto'
      }}>
      {/* En-tête */}
      <div className="express-entry-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <MapPin style={{ width: '32px', height: '32px', color: '#dc2626' }} />
          <h1 style={{ 
            fontSize: '30px', 
            fontWeight: 'bold', 
            color: '#1f2937',
            margin: 0
          }}>
            Calculateur Entrée Express - SCG
          </h1>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            className="express-entry-action express-entry-action--secondary"
            onClick={() => {
              // Dispatch custom event to switch module in Dashboard
              window.dispatchEvent(new CustomEvent('changeModule', { detail: 'admissibilite' }));
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              flexShrink: 0
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
          >
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Retour à l'admissibilité
          </button>
          
          <button
            className="express-entry-action express-entry-action--primary"
            onClick={() => generatePDF()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              flexShrink: 0
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
          >
            <Download style={{ width: '16px', height: '16px' }} />
            Télécharger PDF
          </button>
        </div>
      </div>

      <div style={{ background: '#EFF6FF', padding: '20px', borderRadius: '12px', marginBottom: '20px', color: '#172554' }}>
        <label style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>Grille de calcul
          <select aria-label="Grille de calcul" value={rulesMode} onChange={event => setRulesMode(event.target.value)} style={{ padding: '10px', maxWidth: '100%' }}>
            <option value="auto">Automatique — selon la date du jour</option>
            <option value="previous">Grille jusqu’au 2 novembre 2026</option>
            <option value="november2026">Grille à partir du 3 novembre 2026</option>
          </select>
        </label>
        <p><strong>{rulesLabel}</strong></p>
        {newLabourRules && <p style={{ fontSize: '14px' }}>Estimation selon la réforme annoncée. Le plafond global révisé n’est pas précisé dans l’annonce : le résultat présente la somme des facteurs, sans appliquer l’ancien plafond.</p>}
      </div>
      {/* Score total - Card compacte */}
      <div className="express-entry-score" style={{
        background: 'linear-gradient(to right, #dc2626, #b91c1c)',
        borderRadius: '12px', 
        padding: '24px', 
        marginBottom: '32px',
        color: 'white',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Calculator style={{ width: '48px', height: '48px' }} />
          <div>
            <p style={{ fontSize: '14px', color: '#fecaca', margin: 0 }}>Votre score total SCG</p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <p style={{ fontSize: '42px', fontWeight: 'bold', margin: 0 }}>{totalScore}</p>
              <p style={{ fontSize: '18px', color: '#fecaca', margin: 0 }}>{scoreSuffix}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recherche Client/Lead (optionnel) */}
      <div style={{ marginBottom: '32px' }}>
        <div className="express-entry-lookup" style={{
          backgroundColor: '#F9FAFB',
          borderRadius: '12px', 
          padding: '24px',
          border: '2px dashed #D1D5DB'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <User style={{ width: '20px', height: '20px', color: '#6B7280' }} />
            Calculer pour un client ou un lead (optionnel)
          </h3>
          
          {selectedPerson ? (
            <div style={{ 
              backgroundColor: 'white', 
              padding: '16px', 
              borderRadius: '8px',
              border: '1px solid #D1D5DB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <p style={{ fontWeight: '600', color: '#1F2937', margin: 0 }}>
                  {selectedPerson.label}
                </p>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
                  {selectedPerson.type === 'client' ? 'Client' : 'Lead'}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedPerson(null);
                  setPersonQuery('');
                }}
                style={{
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Retirer
              </button>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={personQuery}
                onChange={handleSelectChange(setPersonQuery)}
                placeholder="Rechercher un client ou un lead par nom, email, téléphone..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#DC2626'}
                onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
              />
              
              {isSearching && (
                <div style={{ 
                  position: 'absolute', 
                  top: '100%', 
                  left: 0, 
                  right: 0, 
                  marginTop: '8px',
                  backgroundColor: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  padding: '16px',
                  textAlign: 'center',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 10
                }}>
                  <p style={{ color: '#6B7280', margin: 0 }}>Recherche en cours...</p>
                </div>
              )}
              
              {!isSearching && personResults.length > 0 && (
                <div style={{ 
                  position: 'absolute', 
                  top: '100%', 
                  left: 0, 
                  right: 0, 
                  marginTop: '8px',
                  backgroundColor: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  zIndex: 10
                }}>
                  {personResults.map((person) => (
                    <div
                      key={`${person.type}-${person.id}`}
                      onClick={() => {
                        setSelectedPerson(person);
                        setPersonResults([]);
                        setPersonQuery('');
                      }}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #F3F4F6',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <p style={{ fontWeight: '600', color: '#1F2937', margin: 0, marginBottom: '4px' }}>
                        {person.label}
                      </p>
                      <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
                        {person.type === 'client' ? '👤 Client' : '🎯 Lead'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px', marginBottom: 0 }}>
            💡 Cette recherche est optionnelle. Elle vous permet d'associer ce calcul à un client ou lead existant.
          </p>
        </div>
      </div>

      <div>
            {/* Introduction */}
            <InfoBox type="info">
              <p style={{ fontWeight: '600', marginBottom: '8px' }}>Bienvenue sur le calculateur Entrée Express!</p>
              <p>Ce calculateur vous aide à estimer votre score selon le Système de classement global (SCG). Pour immigrer au Canada via Entrée Express, vous devez être admissible à l'un des trois programmes fédéraux:</p>
              <ul style={{ listStyleType: 'disc', marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Programme des travailleurs qualifiés (fédéral)</strong></li>
                <li><strong>Programme des travailleurs de métiers spécialisés (fédéral)</strong></li>
                <li><strong>Catégorie de l'expérience canadienne</strong></li>
              </ul>
            </InfoBox>

            {/* Section 1: État matrimonial */}
            <Section title="1. État matrimonial" icon={<User style={{ width: '24px', height: '24px' }} />}>
              <InfoBox type="tip">
                <p><strong>Conseil:</strong> Votre état matrimonial affecte les points disponibles. Si votre époux/conjoint ne vous accompagne pas OU est citoyen/résident permanent canadien, vous recevrez plus de points dans la section A (facteurs de base).</p>
              </InfoBox>
              
              <FormBox>
                <label 
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                  Quel est votre état matrimonial?
                </label>
                <CustomSelect
                  value={maritalStatus}
                  onChange={(newValue) => setMaritalStatus(newValue)}
                  options={[
                    { value: 'single', label: 'Célibataire / Jamais marié(e)' },
                    { value: 'married', label: 'Marié(e)' },
                    { value: 'commonlaw', label: 'Conjoint(e) de fait' },
                    { value: 'divorced', label: 'Divorcé(e)' },
                    { value: 'widowed', label: 'Veuf/Veuve' },
                    { value: 'separated', label: 'Séparé(e) légalement' }
                  ]}
                />
              </FormBox>

              {(maritalStatus === 'married' || maritalStatus === 'commonlaw') && (
                <>
                  <FormBox>
                    <label 
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <input
                        type="checkbox"
                        checked={spouseIsCitizen}
                        onChange={(e) => setSpouseIsCitizen(e.target.checked)}
                        style={{ width: '20px', height: '20px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                        Mon époux/conjoint est citoyen ou résident permanent du Canada
                      </span>
                    </label>
                  </FormBox>

                  {!spouseIsCitizen && (
                    <FormBox>
                      <label 
                        style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <input
                          type="checkbox"
                          checked={spouseAccompanying}
                          onChange={(e) => setSpouseAccompanying(e.target.checked)}
                          style={{ width: '20px', height: '20px' }}
                        />
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                          Mon époux/conjoint m'accompagnera au Canada
                        </span>
                      </label>
                    </FormBox>
                  )}
                </>
              )}
            </Section>

            {/* Section 2: Âge */}
            <Section title="2. Âge" icon={null}>
              <InfoBox type="tip">
                <p><strong>Points maximum:</strong> Entre 20 et 29 ans. Les points diminuent progressivement après 30 ans et deviennent 0 à 45 ans et plus.</p>
              </InfoBox>
              
              <FormBox>
                <label 
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                  Quel âge avez-vous? ({getAgePoints()} points)
                  {Number(age) >= 20 && Number(age) <= 29 && <span style={{ marginLeft: '8px', color: '#16A34A', fontSize: '12px' }}>✓ Points maximum</span>}
                </label>
                <CustomSelect
                  value={age}
                  onChange={(newValue) => setAge(newValue)}
                  options={[
                    { value: '17', label: '17 ans' },
                    { value: '18', label: '18 ans' },
                    { value: '19', label: '19 ans' },
                    { value: '20', label: '20 ans' },
                    { value: '21', label: '21 ans' },
                    { value: '22', label: '22 ans' },
                    { value: '23', label: '23 ans' },
                    { value: '24', label: '24 ans' },
                    { value: '25', label: '25 ans' },
                    { value: '26', label: '26 ans' },
                    { value: '27', label: '27 ans' },
                    { value: '28', label: '28 ans' },
                    { value: '29', label: '29 ans' },
                    { value: '30', label: '30 ans' },
                    { value: '31', label: '31 ans' },
                    { value: '32', label: '32 ans' },
                    { value: '33', label: '33 ans' },
                    { value: '34', label: '34 ans' },
                    { value: '35', label: '35 ans' },
                    { value: '36', label: '36 ans' },
                    { value: '37', label: '37 ans' },
                    { value: '38', label: '38 ans' },
                    { value: '39', label: '39 ans' },
                    { value: '40', label: '40 ans' },
                    { value: '41', label: '41 ans' },
                    { value: '42', label: '42 ans' },
                    { value: '43', label: '43 ans' },
                    { value: '44', label: '44 ans' },
                    { value: '45', label: '45 ans et plus' }
                  ]}
                  placeholder="Sélectionner..."
                />
              </FormBox>
            </Section>

            {/* Section 3: Éducation */}
            <Section title="3. Niveau d'études" icon={<GraduationCap style={{ width: '24px', height: '24px' }} />}>
              <InfoBox type="info">
                <p className="font-semibold mb-2">Important pour obtenir des points:</p>
                <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                  <li>Pour les diplômes <strong>étrangers</strong>: Vous devez faire évaluer vos diplômes par une organisation désignée (EDE)</li>
                  <li>Pour les diplômes <strong>canadiens</strong>: Doit provenir d'un établissement canadien autorisé</li>
                </ul>
              </InfoBox>

              <FormBox>
                <label 
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                  Quel niveau d'études avez-vous atteint? ({getEducationPoints()} points)
                </label>
                <CustomSelect
                  value={education}
                  onChange={(newValue) => setEducation(Number(newValue))}
                  options={[
                    { value: 0, label: 'Études secondaires non complétées' },
                    { value: 28, label: "Diplôme d'études secondaires" },
                    { value: 84, label: "Certificat/diplôme de 1 an (postsecondaire)" },
                    { value: 91, label: "Certificat/diplôme de 2 ans (postsecondaire)" },
                    { value: 112, label: 'Baccalauréat (3 ans ou +)' },
                    { value: 119, label: 'Deux diplômes ou plus (un de 3 ans+)' },
                    { value: 126, label: 'Maîtrise ou diplôme professionnel' },
                    { value: 140, label: 'Doctorat (Ph.D.)' }
                  ]}
                />
              </FormBox>

              <FormBox>
                <label 
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <input
                    type="checkbox"
                    checked={hasCanadianDegree}
                    onChange={(e) => handleCanadianDegreeChange(e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    J'ai obtenu un certificat, grade ou diplôme CANADIEN
                  </span>
                </label>
              </FormBox>

              {hasCanadianDegree && (
                <FormBox>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                    Confirmez toutes les conditions IRCC pour recevoir les points supplémentaires :
                  </p>
                  {[
                    ['languageStudyLimit', "Les cours d'anglais ou de français langue seconde ne représentaient pas plus de la moitié du programme"],
                    ['noReturnAward', "Je n'ai pas étudié grâce à une bourse exigeant mon retour dans mon pays après les études"],
                    ['schoolInCanada', "J'ai étudié dans un établissement situé au Canada (les campus étrangers ne comptent pas)"],
                    ['fullTimeEightMonths', "J'étais inscrit à temps plein pendant au moins 8 mois, ou une exception COVID-19 d'IRCC s'applique"],
                    ['presentInCanadaEightMonths', "J'étais physiquement présent au Canada pendant au moins 8 mois, ou une exception COVID-19 d'IRCC s'applique"]
                  ].map(([requirement, label]) => (
                    <label
                      key={requirement}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', marginBottom: '10px' }}
                    >
                      <input
                        type="checkbox"
                        checked={canadianDegreeRequirements[requirement]}
                        onChange={(e) => handleCanadianDegreeRequirementChange(requirement, e.target.checked)}
                        style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '13px', color: '#374151' }}>{label}</span>
                    </label>
                  ))}
                  {!isCanadianDegreeEligible && (
                    <p style={{ fontSize: '12px', color: '#B45309', marginTop: '8px' }}>
                      Aucun point supplémentaire ne sera accordé tant que toutes les conditions ne sont pas confirmées.
                    </p>
                  )}
                </FormBox>
              )}

              {isCanadianDegreeEligible && (
                <FormBox>
                  <label
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    Quelle est la durée de ce diplôme canadien? ({getCanadianEducationPoints()} points bonus)
                  </label>
                  <CustomSelect
                    value={canadianDegreeLength}
                    onChange={(newValue) => setCanadianDegreeLength(newValue)}
                    options={[
                      { value: 'none', label: 'Sélectionner...' },
                      { value: 'short', label: 'Programme de 1 ou 2 ans (15 points)' },
                      { value: 'long', label: 'Programme de 3 ans ou plus (30 points)' }
                    ]}
                  />
                </FormBox>
              )}
            </Section>

            {/* Section 4: Compétences linguistiques */}
            <Section title="4. Compétences linguistiques" icon={<Languages style={{ width: '24px', height: '24px' }} />}>
              <InfoBox type="info">
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>Tests linguistiques acceptés:</p>
                <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                  <li><strong>Anglais:</strong> IELTS General Training, CELPIP-General ou PTE Core</li>
                  <li><strong>Français:</strong> TEF Canada ou TCF Canada</li>
                  <li>Les résultats doivent dater de moins de 2 ans</li>
                </ul>
                <p style={{ marginTop: '8px' }}><strong>NCLC 7 ou plus</strong> dans les 4 compétences est recommandé pour maximiser vos points!</p>
              </InfoBox>

              <FormBox>
                <label 
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                  Première langue officielle ({getLanguagePoints(languageFirst)} points)
                </label>
                
                <div style={{ marginBottom: '16px' }}>
                  <label 
                      style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                    Quel test linguistique avez-vous passé?
                  </label>
                  <CustomSelect
                    value={firstLanguageTest}
                    onChange={handleFirstLanguageTestChange}
                    options={[
                      { value: 'none', label: 'Sélectionner...' },
                      { value: 'celpip', label: 'CELPIP-General (anglais)' },
                      { value: 'ielts', label: 'IELTS General Training (anglais)' },
                      { value: 'pte', label: 'PTE Core (anglais)' },
                      { value: 'tef', label: 'TEF Canada (français)' },
                      { value: 'tcf', label: 'TCF Canada (français)' }
                    ]}
                    style={{ padding: '8px' }}
                  />
                </div>

                {firstLanguageTest !== 'none' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {['reading', 'writing', 'speaking', 'listening'].map((skill) => {
                      const isFrench = firstLanguageTest === 'tef' || firstLanguageTest === 'tcf';
                      const isIELTS = firstLanguageTest === 'ielts';
                      const isCELPIP = firstLanguageTest === 'celpip';
                      const isPTE = firstLanguageTest === 'pte';
                      const isNumericInput = isFrench || isIELTS || isCELPIP || isPTE; // Tous nécessitent saisie numérique
                      
                      // Labels différents pour tests français vs anglais
                      let skillLabel;
                      if (isFrench) {
                        // Tests français : Terminologie officielle
                        skillLabel = skill === 'reading' ? 'Compréhension de l\'écrit' : 
                                    skill === 'writing' ? 'Expression écrite' : 
                                    skill === 'speaking' ? 'Expression orale' : 'Compréhension orale';
                      } else if (isIELTS || isCELPIP || isPTE) {
                        // Tests anglais : Labels en anglais
                        skillLabel = skill === 'reading' ? 'Reading' : 
                                    skill === 'writing' ? 'Writing' : 
                                    skill === 'speaking' ? 'Speaking' : 'Listening';
                      } else {
                        // Par défaut (ne devrait pas arriver)
                        skillLabel = skill === 'reading' ? 'Lecture' : 
                                    skill === 'writing' ? 'Écriture' : 
                                    skill === 'speaking' ? 'Expression orale' : 'Compréhension orale';
                      }
                      
                      // Plages de scores selon le test
                      const getScoreRange = () => {
                        if (firstLanguageTest === 'tef') {
                          if (skill === 'reading') return { min: 0, max: 300, placeholder: 'Ex: 250', step: 1 };
                          if (skill === 'writing') return { min: 0, max: 450, placeholder: 'Ex: 350', step: 1 };
                          if (skill === 'listening') return { min: 0, max: 360, placeholder: 'Ex: 280', step: 1 };
                          if (skill === 'speaking') return { min: 0, max: 450, placeholder: 'Ex: 350', step: 1 };
                        } else if (firstLanguageTest === 'tcf') {
                          if (skill === 'reading' || skill === 'listening') return { min: 0, max: 699, placeholder: 'Ex: 500', step: 1 };
                          if (skill === 'writing' || skill === 'speaking') return { min: 0, max: 20, placeholder: 'Ex: 12', step: 1 };
                        } else if (firstLanguageTest === 'ielts') {
                          return { min: 0, max: 9, placeholder: 'Ex: 7.5', step: 0.5 };
                        } else if (firstLanguageTest === 'celpip') {
                          return { min: 0, max: 12, placeholder: 'Ex: 9', step: 1 };
                        } else if (firstLanguageTest === 'pte') {
                          const placeholders = { reading: 'Ex: 78', writing: 'Ex: 88', speaking: 'Ex: 84', listening: 'Ex: 82' };
                          return { min: 0, max: 90, placeholder: placeholders[skill], step: 1 };
                        }
                        return { min: 0, max: 10, placeholder: '', step: 1 };
                      };
                      
                      const range = getScoreRange();
                      
                      return (
                        <div key={skill}>
                          <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                            {skillLabel}
                            {isNumericInput && ` (CLB ${languageFirst[skill] || 0})`}
                          </label>
                          {isNumericInput ? (
                            <input
                              type="number"
                              min={range.min}
                              max={range.max}
                              step={range.step}
                              placeholder={range.placeholder}
                              value={languageFirstScores[skill]}
                              key={`first-${skill}-${firstLanguageTest}`}
                              onChange={(e) => {
                                // Mise à jour immédiate du score et de sa conversion CLB/NCLC.
                                const score = e.target.value;
                                const scoreNum = Number(score) || 0;
                                let clb = 0;
                                
                                if (firstLanguageTest === 'tef') {
                                  clb = convertTEFtoNCLC(scoreNum, skill);
                                } else if (firstLanguageTest === 'tcf') {
                                  clb = convertTCFtoNCLC(scoreNum, skill);
                                } else if (firstLanguageTest === 'ielts') {
                                  clb = convertIELTStoCLBBySkill(scoreNum, skill);
                                } else if (firstLanguageTest === 'celpip') {
                                  clb = convertCELPIPtoCLB(scoreNum);
                                } else if (firstLanguageTest === 'pte') {
                                  clb = convertPTECoreToCLB(scoreNum, skill);
                                }
                                
                                setLanguageFirstScores((previousScores) => ({...previousScores, [skill]: score}));
                                setLanguageFirst((previousLevels) => ({...previousLevels, [skill]: clb}));
                              }}
                              className="form-control"
                              style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
                            />
                          ) : (
                            <CustomSelect
                              value={languageFirst[skill]}
                              onChange={(newValue) => setLanguageFirst({
                                ...languageFirst,
                                [skill]: Number(newValue)
                              })}
                              options={[0, 4, 5, 6, 7, 8, 9, 10].map((level) => ({
                                value: level,
                                label: level === 0 ? 'Aucun' : `NCLC/CLB ${level}`
                              }))}
                              style={{ padding: '8px' }}
                              optionHeight={38}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </FormBox>

              <FormBox>
                <label 
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                  Deuxième langue officielle ({getSecondLanguagePoints(languageSecond)} points)
                </label>
                
                <InfoBox type="tip">
                  <p><strong>Bonus bilingue:</strong> Si vous maîtrisez le français (NCLC 7+) ET l'anglais (CLB 5+), vous pouvez obtenir jusqu'à <strong>50 points supplémentaires</strong>!</p>
                </InfoBox>
                
                <div style={{ marginBottom: '16px' }}>
                  <label 
                      style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                    Avez-vous passé un test pour une deuxième langue officielle?
                  </label>
                  <CustomSelect
                    value={secondLanguageTest}
                    onChange={handleSecondLanguageTestChange}
                    options={secondLanguageOptions}
                    style={{ padding: '8px' }}
                  />
                </div>

                {secondLanguageTest !== 'none' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {['reading', 'writing', 'speaking', 'listening'].map((skill) => {
                      const isFrench = secondLanguageTest === 'tef' || secondLanguageTest === 'tcf';
                      const isIELTS = secondLanguageTest === 'ielts';
                      const isCELPIP = secondLanguageTest === 'celpip';
                      const isPTE = secondLanguageTest === 'pte';
                      const isNumericInput = isFrench || isIELTS || isCELPIP || isPTE;
                      
                      // Labels différents pour tests français vs anglais
                      let skillLabel;
                      if (isFrench) {
                        // Tests français : Terminologie officielle
                        skillLabel = skill === 'reading' ? 'Compréhension de l\'écrit' : 
                                    skill === 'writing' ? 'Expression écrite' : 
                                    skill === 'speaking' ? 'Expression orale' : 'Compréhension orale';
                      } else if (isIELTS || isCELPIP || isPTE) {
                        // Tests anglais : Labels en anglais
                        skillLabel = skill === 'reading' ? 'Reading' : 
                                    skill === 'writing' ? 'Writing' : 
                                    skill === 'speaking' ? 'Speaking' : 'Listening';
                      } else {
                        // Par défaut (ne devrait pas arriver)
                        skillLabel = skill === 'reading' ? 'Lecture' : 
                                    skill === 'writing' ? 'Écriture' : 
                                    skill === 'speaking' ? 'Expression orale' : 'Compréhension orale';
                      }
                      
                      // Plages de scores selon le test
                      const getScoreRange = () => {
                        if (secondLanguageTest === 'tef') {
                          if (skill === 'reading') return { min: 0, max: 300, step: 1, placeholder: 'Ex: 250' };
                          if (skill === 'writing') return { min: 0, max: 450, step: 1, placeholder: 'Ex: 350' };
                          if (skill === 'listening') return { min: 0, max: 360, step: 1, placeholder: 'Ex: 280' };
                          if (skill === 'speaking') return { min: 0, max: 450, step: 1, placeholder: 'Ex: 350' };
                        } else if (secondLanguageTest === 'tcf') {
                          if (skill === 'reading' || skill === 'listening') return { min: 0, max: 699, step: 1, placeholder: 'Ex: 500' };
                          if (skill === 'writing' || skill === 'speaking') return { min: 0, max: 20, step: 1, placeholder: 'Ex: 12' };
                        } else if (secondLanguageTest === 'ielts') {
                          return { min: 0, max: 9, step: 0.5, placeholder: 'Ex: 7.5' };
                        } else if (secondLanguageTest === 'celpip') {
                          return { min: 0, max: 12, step: 1, placeholder: 'Ex: 9' };
                        } else if (secondLanguageTest === 'pte') {
                          const placeholders = { reading: 'Ex: 78', writing: 'Ex: 88', speaking: 'Ex: 84', listening: 'Ex: 82' };
                          return { min: 0, max: 90, step: 1, placeholder: placeholders[skill] };
                        }
                        return { min: 0, max: 10, step: 1, placeholder: '' };
                      };
                      
                      const range = getScoreRange();
                      
                      return (
                        <div key={skill}>
                          <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                            {skillLabel}
                            {isNumericInput && ` (CLB ${languageSecond[skill] || 0})`}
                          </label>
                          {isNumericInput ? (
                            <input
                              type="number"
                              min={range.min}
                              max={range.max}
                              step={range.step}
                              placeholder={range.placeholder}
                              value={languageSecondScores[skill]}
                              key={`second-${skill}-${secondLanguageTest}`}
                              onChange={(e) => {
                                // Mise à jour immédiate du score et de sa conversion CLB/NCLC.
                                const score = e.target.value;
                                const scoreNum = Number(score) || 0;
                                let clb = 0;
                                
                                if (secondLanguageTest === 'tef') {
                                  clb = convertTEFtoNCLC(scoreNum, skill);
                                } else if (secondLanguageTest === 'tcf') {
                                  clb = convertTCFtoNCLC(scoreNum, skill);
                                } else if (secondLanguageTest === 'ielts') {
                                  clb = convertIELTStoCLBBySkill(scoreNum, skill);
                                } else if (secondLanguageTest === 'celpip') {
                                  clb = convertCELPIPtoCLB(scoreNum);
                                } else if (secondLanguageTest === 'pte') {
                                  clb = convertPTECoreToCLB(scoreNum, skill);
                                }
                                
                                setLanguageSecondScores((previousScores) => ({...previousScores, [skill]: score}));
                                setLanguageSecond((previousLevels) => ({...previousLevels, [skill]: clb}));
                              }}
                              className="form-control"
                              style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
                            />
                          ) : (
                            <CustomSelect
                              value={languageSecond[skill]}
                              onChange={(newValue) => setLanguageSecond({
                                ...languageSecond,
                                [skill]: Number(newValue)
                              })}
                              options={[0, 4, 5, 6, 7, 8, 9, 10].map((level) => ({
                                value: level,
                                label: level === 0 ? 'Aucun' : `NCLC/CLB ${level}`
                              }))}
                              style={{ padding: '8px' }}
                              optionHeight={38}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </FormBox>
            </Section>

            {/* Section 5: Expérience de travail */}
            <Section title="5. Expérience de travail" icon={<Briefcase style={{ width: '24px', height: '24px' }} />}>
              <InfoBox type="info">
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>Qu'est-ce qu'un travail qualifié?</p>
                <p>Selon la Classification nationale des professions (CNP 2021):</p>
                <ul style={{ listStyleType: 'disc', marginLeft: '20px', marginTop: '8px' }}>
                  <li><strong>FEER 0:</strong> Postes de gestion</li>
                  <li><strong>FEER 1:</strong> Professions nécessitant un diplôme universitaire</li>
                  <li><strong>FEER 2:</strong> Professions nécessitant un diplôme collégial</li>
                  <li><strong>FEER 3:</strong> Professions nécessitant un diplôme d'études secondaires</li>
                </ul>
              </InfoBox>

              <FormBox>
                <label 
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                  Expérience de travail qualifié au Canada ({labourResult.duration} points de durée)
                </label>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>Au cours des 10 dernières années</p>
                <CustomSelect
                  value={canadianExp}
                  onChange={(newValue) => setCanadianExp(Number(newValue))}
                  options={[
                    { value: 0, label: "Aucune ou moins d'un an" },
                    { value: 1, label: '1 an' },
                    { value: 2, label: '2 ans' },
                    { value: 3, label: '3 ans' },
                    { value: 4, label: '4 ans' },
                    { value: 5, label: '5 ans ou plus' }
                  ]}
                />
              </FormBox>

              <FormBox>
                <label 
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                  Expérience de travail qualifié à l'étranger
                </label>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>Au cours des 10 dernières années</p>
                <CustomSelect
                  value={foreignExp}
                  onChange={(newValue) => setForeignExp(Number(newValue))}
                  options={[
                    { value: 0, label: 'Aucune' },
                    { value: 1, label: '1 an' },
                    { value: 2, label: '2 ans' },
                    { value: 3, label: '3 ans ou plus' }
                  ]}
                />
              </FormBox>
            </Section>

            {/* Section 6: Certificat de compétence */}
            <Section title="6. Certificat de compétence" icon={<Award style={{ width: '24px', height: '24px' }} />}>
              <InfoBox type="info">
                <p>Un certificat de compétence permet d'exercer certains <strong>métiers spécialisés</strong> au Canada (ex: électricien, plombier, soudeur).</p>
              </InfoBox>

              <FormBox>
                <label 
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <input
                    type="checkbox"
                    checked={hasCertificate}
                    onChange={(e) => setHasCertificate(e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    J'ai un certificat de compétence provincial/territorial (métier spécialisé)
                  </span>
                </label>
              </FormBox>
            </Section>

            <ExpressEntryLabourMarket
              newRules={newLabourRules} result={labourResult}
              experience={labourExperience} setExperience={setLabourExperience}
              experienceConfirmed={experienceConfirmed} setExperienceConfirmed={setExperienceConfirmed}
              hasJobOffer={hasJobOffer} setHasJobOffer={setHasJobOffer}
              offerCode={offerCode} setOfferCode={setOfferCode}
            />

            {/* Section 8: Frère ou sœur */}
            <Section title="8. Frère ou sœur au Canada" icon={null}>
              <InfoBox type="tip">
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>Critères pour obtenir les 15 points:</p>
                <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                  <li>Être âgé(e) de 18 ans ou plus</li>
                  <li>Être citoyen canadien ou résident permanent</li>
                  <li>Vivre actuellement au Canada</li>
                </ul>
              </InfoBox>

              <FormBox>
                <label 
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <input
                    type="checkbox"
                    checked={hasSibling}
                    onChange={(e) => setHasSibling(e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    J'ai un frère ou une sœur au Canada (citoyen/résident permanent, 18+) - 15 points
                  </span>
                </label>
              </FormBox>
            </Section>

            {/* Section 9: Facteurs du conjoint */}
            {hasSpouse && (
              <Section title="9. Informations sur l'époux/conjoint" icon={<User style={{ width: '24px', height: '24px' }} />}>
                <InfoBox type="info">
                  <p>Les facteurs de votre époux/conjoint peuvent ajouter jusqu'à <strong>40 points</strong> à votre score total.</p>
                </InfoBox>

                <FormBox>
                  <label 
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    Niveau de scolarité de l'époux/conjoint ({getSpouseEducationPoints()} points)
                  </label>
                  <CustomSelect
                    value={spouseEducation}
                    onChange={(newValue) => setSpouseEducation(Number(newValue))}
                    options={[
                      { value: 0, label: 'Études secondaires non complétées' },
                      { value: 2, label: "Diplôme d'études secondaires" },
                      { value: 6, label: "Certificat/diplôme de 1 an" },
                      { value: 7, label: "Certificat/diplôme de 2 ans" },
                      { value: 8, label: 'Baccalauréat (3 ans ou +)' },
                      { value: 9, label: 'Deux diplômes ou plus' },
                      { value: 10, label: 'Maîtrise ou doctorat' }
                    ]}
                  />
                </FormBox>

                <FormBox>
                  <label 
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    Expérience canadienne de l'époux/conjoint ({getSpouseCanadianExpPoints()} points)
                  </label>
                  <CustomSelect
                    value={spouseCanadianExp}
                    onChange={(newValue) => setSpouseCanadianExp(Number(newValue))}
                    options={[
                      { value: 0, label: "Aucune ou moins d'un an" },
                      { value: 1, label: '1 an' },
                      { value: 2, label: '2 ans' },
                      { value: 3, label: '3 ans' },
                      { value: 4, label: '4 ans' },
                      { value: 5, label: '5 ans ou plus' }
                    ]}
                  />
                </FormBox>

                <FormBox>
                  <label 
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    Test linguistique de l'époux/conjoint ({getSpouseLanguagePoints()} points)
                  </label>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label 
                      style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      Quel test a-t-il/elle passé?
                    </label>
                    <CustomSelect
                      value={spouseLanguageTest}
                      onChange={(newValue) => setSpouseLanguageTest(newValue)}
                      options={[
                        { value: 'none', label: 'Sans objet' },
                        { value: 'celpip', label: 'CELPIP-General (anglais)' },
                        { value: 'ielts', label: 'IELTS General Training (anglais)' },
                        { value: 'pte', label: 'PTE Core (anglais)' },
                        { value: 'tef', label: 'TEF Canada (français)' },
                        { value: 'tcf', label: 'TCF Canada (français)' }
                      ]}
                      style={{ padding: '8px' }}
                    />
                  </div>

                  {spouseLanguageTest !== 'none' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      {['reading', 'writing', 'speaking', 'listening'].map((skill) => {
                        const options = [0, 4, 5, 6, 7, 8, 9, 10].map((level) => ({
                          value: level,
                          label: level === 0 ? 'Aucun' : `NCLC ${level}`
                        }));

                        return (
                          <div key={skill}>
                            <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px', textTransform: 'capitalize' }}>
                              {skill === 'reading' ? 'Lecture' : 
                               skill === 'writing' ? 'Écriture' : 
                               skill === 'speaking' ? 'Expression orale' : 'Compréhension orale'}
                            </label>
                            <CustomSelect
                              value={spouseLanguage[skill]}
                              onChange={(newValue) => setSpouseLanguage({
                                ...spouseLanguage,
                                [skill]: Number(newValue)
                              })}
                              options={options}
                              style={{ padding: '8px' }}
                              optionHeight={38}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </FormBox>
              </Section>
            )}

            {/* Section 10: Désignation provinciale */}
            <Section title="10. Désignation provinciale/territoriale" icon={null}>
              <InfoBox type="tip">
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>🎯 Boost de 600 points!</p>
                <p>Une désignation d'une province ou d'un territoire (via un Programme des candidats des provinces - PCP) vous garantit pratiquement une invitation à présenter une demande.</p>
              </InfoBox>

              <FormBox>
                <label 
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <input
                    type="checkbox"
                    checked={provincialNomination}
                    onChange={(e) => setProvincialNomination(e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    J'ai une désignation d'une province ou d'un territoire - 600 points
                  </span>
                </label>
              </FormBox>
            </Section>

            {/* Résumé final */}
            <section style={{ 
              background: 'linear-gradient(to bottom right, #F0FDF4, #DCFCE7)', 
              border: '2px solid #86EFAC', 
              borderRadius: '16px', 
              padding: '32px',
              marginTop: '32px'
            }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1F2937', marginBottom: '24px', textAlign: 'center' }}>
                📊 Vos résultats - Récapitulatif
              </h2>

              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '600', color: '#374151' }}>
                    {newLabourRules ? 'Capital humain et intégration au marché du travail' : 'Facteurs de base/du capital humain'}
                  </span>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#DC2626' }}>
                    {getAgePoints() + getEducationPoints() + getLanguagePoints(languageFirst) + 
                     getSecondLanguagePoints(languageSecond) + getCanadianExpPoints()}
                  </span>
                </div>
              </div>

              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '600', color: '#374151' }}>
                    Facteurs de l'époux ou du conjoint de fait
                  </span>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#DC2626' }}>
                    {getSpouseEducationPoints() + getSpouseLanguagePoints() + getSpouseCanadianExpPoints()}
                  </span>
                </div>
              </div>

              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '600', color: '#374151' }}>
                    Facteurs liés au transfert des compétences
                  </span>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#DC2626' }}>
                    {getTransferabilityPoints()}
                  </span>
                </div>
              </div>

              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '600', color: '#374151' }}>
                    Points supplémentaires
                  </span>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#DC2626' }}>
                    {getAdditionalPoints()}
                  </span>
                </div>
              </div>

              <div style={{ 
                background: 'linear-gradient(to right, #DC2626, #B91C1C)', 
                padding: '24px', 
                borderRadius: '12px', 
                color: 'white',
                marginTop: '24px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    Score total SCG
                  </span>
                  <span style={{ fontSize: '48px', fontWeight: 'bold' }}>
                    {totalScore}
                  </span>
                </div>
                <p style={{ color: '#FECACA', fontSize: '14px', marginTop: '8px' }}>
                  {newLabourRules ? rulesLabel : 'Maximum possible : 1200 points'}
                </p>
              </div>
            </section>

            {/* Interprétation du score */}
            <section style={{ marginTop: '32px' }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: 'bold', 
                color: '#1F2937', 
                borderBottom: '2px solid #DC2626', 
                paddingBottom: '8px',
                marginBottom: '16px'
              }}>
                💡 Interprétation de votre score
              </h2>

              {totalScore >= 500 && (
                <InfoBox type="tip">
                  <p style={{ fontWeight: '600', color: '#15803D' }}>Excellent score! ({totalScore} points)</p>
                  <p>Avec un score de {totalScore} points, vous avez d'excellentes chances de recevoir une invitation lors des prochaines rondes. Continuez à surveiller les rondes d'invitations sur le site de l'IRCC.</p>
                </InfoBox>
              )}

              {totalScore >= 450 && totalScore < 500 && (
                <InfoBox type="tip">
                  <p style={{ fontWeight: '600', color: '#1E40AF' }}>Bon score! ({totalScore} points)</p>
                  <p>Votre score est compétitif. Considérez ces options pour l'améliorer:</p>
                  <ul style={{ listStyleType: 'disc', marginLeft: '20px', marginTop: '4px' }}>
                    <li>Améliorer vos résultats linguistiques (viser NCLC 9+)</li>
                    <li>Obtenir plus d'expérience de travail canadienne</li>
                    <li>Explorer les programmes des candidats des provinces (PCP)</li>
                  </ul>
                </InfoBox>
              )}

              {totalScore >= 350 && totalScore < 450 && (
                <InfoBox type="warning">
                  <p style={{ fontWeight: '600', color: '#92400E' }}>Score moyen ({totalScore} points)</p>
                  <p>Votre score pourrait nécessiter une amélioration. Recommandations:</p>
                  <ul style={{ listStyleType: 'disc', marginLeft: '20px', marginTop: '4px' }}>
                    <li>Passez (ou repassez) vos tests linguistiques pour obtenir NCLC 7+</li>
                    <li>Obtenez une EDE pour vos diplômes étrangers si pas déjà fait</li>
                    <li>Gagnez de l'expérience de travail qualifié</li>
                    <li>Explorez fortement les PCP - certaines provinces ont des seuils plus bas</li>
                  </ul>
                </InfoBox>
              )}

              {totalScore < 350 && (
                <InfoBox type="warning">
                  <p style={{ fontWeight: '600', color: '#92400E' }}>Score à améliorer ({totalScore} points)</p>
                  <p>Votre score actuel est en dessous des seuils typiques. Actions recommandées:</p>
                  <ul style={{ listStyleType: 'disc', marginLeft: '20px', marginTop: '4px' }}>
                    <li>Améliorez significativement vos compétences linguistiques (priorité #1)</li>
                    <li>Obtenez plus d'éducation ou faites évaluer vos diplômes</li>
                    <li>Gagnez de l'expérience de travail qualifié (au Canada si possible)</li>
                    <li>Recherchez des programmes provinciaux avec critères spécifiques</li>
                    <li>Considérez d'autres voies d'immigration (permis de travail, études au Canada)</li>
                  </ul>
                </InfoBox>
              )}
            </section>

            {/* Avis de non-responsabilité */}
            <div style={{ 
              backgroundColor: '#eff6ff', 
              borderLeft: '4px solid #3b82f6', 
              padding: '24px', 
              borderRadius: '8px',
              marginTop: '32px'
            }}>
              <p style={{ fontSize: '14px', color: '#1e40af', marginBottom: '8px' }}>
                <strong>Note importante — estimation personnalisée:</strong> Ce calculateur fournit une estimation basée sur vos informations et la grille sélectionnée. Une simulation de la grille annoncée ne constitue pas une note officielle. Une invitation à présenter une demande (IPD) ne peut être garantie. Les seuils et critères d'IRCC peuvent changer à chaque ronde.
              </p>
              <p style={{ fontSize: '14px', color: '#1e40af' }}>
                <strong>Note:</strong> {newLabourRules ? 'L’expérience canadienne et l’offre d’emploi sont comparées dans un même facteur; seul le meilleur résultat est retenu.' : 'Jusqu’au 2 novembre 2026, les offres d’emploi ne donnent aucun point SCG, mais peuvent être requises pour certains programmes.'}
              </p>
            </div>

            {/* Bouton Télécharger PDF en bas */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginTop: '32px',
              paddingBottom: '24px'
            }}>
              <button
                onClick={() => generatePDF()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '16px 32px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#b91c1c';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                <Download style={{ width: '20px', height: '20px' }} />
                Télécharger le rapport PDF détaillé
              </button>
            </div>
      </div>
      </div>
    </div>
  );
}
